import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useFacilities } from "@/context/FacilityContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { ArrowDown, FileArrowDown, TrendUp, TrendDown } from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const SCOPE_COLORS = { "1": "#274E37", "2": "#1A5F7A", "3": "#D97706" };

export default function Dashboard() {
  const { t } = useLang();
  const { facilities } = useFacilities();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [facilityId, setFacilityId] = useState("");
  const [targets, setTargets] = useState([]);
  const [yoy, setYoy] = useState([]);
  const [intensity, setIntensity] = useState(null);
  const [verification, setVerification] = useState(null);

  const load = async (y, fid) => {
    const params = { year: y };
    if (fid) params.facility_id = fid;
    const [d, tg, yy, its, ver] = await Promise.all([
      api.get(`/dashboard`, { params }),
      api.get("/targets"),
      api.get("/yoy"),
      api.get("/intensity/summary", { params: { year: y } }),
      api.get("/verifications/current", { params: { year: y } }),
    ]);
    setData(d.data);
    setTargets(tg.data);
    setYoy(yy.data);
    setIntensity(its.data);
    setVerification(ver.data);
  };

  useEffect(() => { load(year, facilityId); }, [year, facilityId]);

  const nav = useNavigate();
  const queuePdf = async () => {
    try {
      const payload = { year };
      if (facilityId) payload.facility_id = facilityId;
      await api.post("/jobs/export_pdf", payload);
      toast.success("PDF queued — check Jobs");
      nav("/app/jobs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const download = (path, filename) => {
    const params = new URLSearchParams();
    params.set("year", year);
    if (facilityId) params.set("facility_id", facilityId);
    const url = `${API_BASE}${path}?${params.toString()}`;
    const token = localStorage.getItem("cl_token") || "";
    const orgId = localStorage.getItem("cl_active_org") || "";
    fetch(url, {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(orgId ? { "X-Org-Id": orgId } : {}),
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      });
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const scopeChart = [1, 2, 3].map((s) => ({
    name: `${t("scope")} ${s}`,
    value: (data.by_scope[String(s)] || 0) / 1000,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-3">
            <span>{t("dashboard")} · {year}</span>
            {verification && verification.status !== "unverified" && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm normal-case ${
                verification.status === "verified" ? "bg-primary/10 text-primary" :
                verification.status === "in_review" ? "bg-accent/10 text-accent" :
                verification.status === "qualified" ? "bg-yellow-500/10 text-yellow-700" :
                "bg-destructive/10 text-destructive"
              }`} data-testid="dash-verification-badge">
                {t(verification.status.replace("_", "")) || verification.status}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl font-semibold mt-1">{t("totalEmissions")}</h1>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1.5">
            <Label htmlFor="facility-filter" data-testid="facility-filter-label" className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
              {t("facilityFilterLabel")}
            </Label>
            <Select value={facilityId || "all"} onValueChange={(v) => setFacilityId(v === "all" ? "" : v)}>
              <SelectTrigger id="facility-filter" className="w-[220px]" data-testid="facility-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allFacilities")}</SelectItem>
                {facilities.map((f) => (
                  <SelectItem key={`dash-fac-${f.id}`} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] font-mono-data" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map((n) => {
                const y = new Date().getFullYear() - n;
                return <SelectItem key={`dash-y-${y}`} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => download("/export/csv", `carbon_${year}.csv`)} data-testid="export-csv">
            <ArrowDown size={16} className="mr-2" /> {t("exportCsv")}
          </Button>
          <Button onClick={queuePdf} data-testid="export-pdf">
            <FileArrowDown size={16} className="mr-2" /> {t("generatePdfAsync")}
          </Button>
        </div>
      </div>

      {/* Top KPI */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-2 border-primary/30" data-testid="kpi-total">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("totalEmissions")}</div>
            <div className="font-display text-5xl lg:text-6xl font-semibold mt-2 leading-none">
              {data.total_t_co2e.toLocaleString()}
              <span className="text-2xl text-muted-foreground ml-2 font-mono-data">{t("tCO2e")}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-3 font-mono-data">
              {data.entry_count} {t("records")} · {data.total_kg_co2e.toLocaleString()} {t("kgCO2e")}
            </div>
          </CardContent>
        </Card>
        {[1, 2, 3].map((s) => (
          <Card key={s} data-testid={`kpi-scope${s}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">
                  {t(`scope${s}`)}
                </div>
                <div className="w-2 h-2 rounded-full" style={{ background: SCOPE_COLORS[String(s)] }} />
              </div>
              <div className="font-display text-3xl font-semibold">
                {((data.by_scope[String(s)] || 0) / 1000).toFixed(3)}
                <span className="text-sm text-muted-foreground ml-1 font-mono-data">{t("tCO2e")}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {data.total_kg_co2e > 0
                  ? `${(((data.by_scope[String(s)] || 0) / data.total_kg_co2e) * 100).toFixed(1)}%`
                  : "0%"}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("monthlyTrend")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={data.by_month.map((m) => ({
                  month: m.month,
                  Scope1: m.scope1 / 1000,
                  Scope2: m.scope2 / 1000,
                  Scope3: m.scope3 / 1000,
                }))}>
                  <CartesianGrid stroke="#E2E8E4" strokeDasharray="3 3" />
                  <XAxis dataKey="month" style={{ fontSize: 11 }} />
                  <YAxis style={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Scope1" stackId="a" fill={SCOPE_COLORS["1"]} />
                  <Bar dataKey="Scope2" stackId="a" fill={SCOPE_COLORS["2"]} />
                  <Bar dataKey="Scope3" stackId="a" fill={SCOPE_COLORS["3"]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("breakdownByScope")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={scopeChart} layout="vertical">
                  <XAxis type="number" style={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={80} style={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {scopeChart.map((_, i) => (
                      <cell key={i} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Intensity ratios (if data available) */}
      {intensity?.available && intensity.intensities && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">Intensity ratios · {year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {intensity.intensities.kg_co2e_per_currency != null && (
                <RatioCard label={t("perRevenue")} value={intensity.intensities.kg_co2e_per_currency} unit={`kg/${intensity.revenue_currency}`} testid="dash-ratio-revenue" />
              )}
              {intensity.intensities.t_co2e_per_fte != null && (
                <RatioCard label={t("perFte")} value={intensity.intensities.t_co2e_per_fte} unit="tCO₂e/FTE" testid="dash-ratio-fte" />
              )}
              {intensity.intensities.kg_co2e_per_m2 != null && (
                <RatioCard label={t("perArea")} value={intensity.intensities.kg_co2e_per_m2} unit="kgCO₂e/m²" testid="dash-ratio-area" />
              )}
              {intensity.intensities.kg_co2e_per_production_unit != null && (
                <RatioCard label={t("perProduction")} value={intensity.intensities.kg_co2e_per_production_unit} unit={`kg/${intensity.production_unit_name || "unit"}`} testid="dash-ratio-prod" />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("breakdownByCategory")}</CardTitle>
        </CardHeader>
        <CardContent>
          {(!data.by_category || data.by_category.length === 0) ? (
            <div className="text-sm text-muted-foreground py-8 text-center">{t("noEntries")}</div>
          ) : (
            <div className="space-y-2">
              {data.by_category.slice(0, 10).map((c) => {
                const pct = data.total_kg_co2e > 0 ? (c.value / data.total_kg_co2e) * 100 : 0;
                return (
                  <div key={c.name} className="flex items-center gap-4">
                    <div className="w-56 truncate text-sm">{c.name}</div>
                    <div className="flex-1 h-6 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-32 text-right font-mono-data text-sm">
                      {(c.value / 1000).toFixed(3)} {t("tCO2e")}
                    </div>
                    <div className="w-14 text-right font-mono-data text-xs text-muted-foreground">
                      {pct.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by facility */}
      {data.by_facility && data.by_facility.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("breakdownByFacility")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.by_facility.slice(0, 10).map((f) => {
                const pct = data.total_kg_co2e > 0 ? (f.value / data.total_kg_co2e) * 100 : 0;
                return (
                  <div key={f.name} className="flex items-center gap-4">
                    <div className="w-56 truncate text-sm">{f.name}</div>
                    <div className="flex-1 h-6 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-primary/70" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-32 text-right font-mono-data text-sm">
                      {(f.value / 1000).toFixed(3)} {t("tCO2e")}
                    </div>
                    <div className="w-14 text-right font-mono-data text-xs text-muted-foreground">
                      {pct.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* YoY Comparison */}
      {yoy.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("yoyComparison")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={yoy}>
                  <CartesianGrid stroke="#E2E8E4" strokeDasharray="3 3" />
                  <XAxis dataKey="year" style={{ fontSize: 11 }} />
                  <YAxis style={{ fontSize: 11 }} label={{ value: "tCO₂e", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#274E37" strokeWidth={3} dot={{ r: 5 }} name="Total" />
                  <Line type="monotone" dataKey="scope1" stroke="#274E37" strokeDasharray="4 4" name="Scope 1" />
                  <Line type="monotone" dataKey="scope2" stroke="#1A5F7A" strokeDasharray="4 4" name="Scope 2" />
                  <Line type="monotone" dataKey="scope3" stroke="#D97706" strokeDasharray="4 4" name="Scope 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
              {yoy.slice(-4).map((y) => (
                <div key={y.year} className="border border-border rounded-md p-3">
                  <div className="text-xs text-muted-foreground font-mono-data">{y.year}</div>
                  <div className="font-display text-xl font-semibold font-mono-data">{y.total.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground font-mono-data">
                    {y.yoy_change_pct !== null && (
                      <span className={`inline-flex items-center gap-1 ${y.yoy_change_pct > 0 ? "text-destructive" : "text-primary"}`}>
                        {y.yoy_change_pct > 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
                        {y.yoy_change_pct > 0 ? "+" : ""}{y.yoy_change_pct}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SBTi Targets progress */}
      {targets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("sbtiAligned")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {targets.slice(0, 3).map((tg) => {
              const progress = Math.max(0, Math.min(100, tg.progress_pct));
              return (
                <div key={tg.id} className="border border-border rounded-md p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-display font-medium">{tg.name}</div>
                      <div className="text-xs text-muted-foreground font-mono-data">
                        {tg.baseline_year} → {tg.target_year} · −{tg.reduction_pct}% · {tg.scopes.map((s) => `S${s}`).join(" + ")}
                      </div>
                    </div>
                    <span className={`text-[10px] font-mono-data uppercase px-2 py-1 rounded-sm ${tg.on_track ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                      {tg.on_track ? t("onTrack") : t("offTrack")}
                    </span>
                  </div>
                  <div className="h-2 bg-secondary rounded-sm overflow-hidden">
                    <div className={`h-full ${tg.on_track ? "bg-primary" : "bg-accent"}`} style={{ width: `${progress}%` }} />
                  </div>
                  <div className="flex justify-between text-xs font-mono-data mt-2 text-muted-foreground">
                    <span>{t("baseline")}: {tg.baseline_emissions_t_co2e.toFixed(2)}</span>
                    <span className="font-medium text-foreground">{tg.current_emissions_t_co2e.toFixed(2)} / {tg.target_emissions_t_co2e.toFixed(2)} tCO₂e</span>
                    <span>{tg.progress_pct.toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
            <Link to="/app/targets" className="text-sm text-primary hover:underline" data-testid="link-targets">
              → {t("targets")}
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const RatioCard = ({ label, value, unit, testid }) => (
  <div className="border border-border rounded-md p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data capitalize">{label}</div>
    <div className="font-display text-2xl font-semibold font-mono-data mt-1">{value}</div>
    <div className="text-[10px] text-muted-foreground font-mono-data">{unit}</div>
  </div>
);

